import { useCallback, useEffect, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";

import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { useLocation, useNavigate } from "react-router";
import { WizardNew as Wizard } from "../../../../components/wizard";
import Section from "../../../../components/wizard/Section";
import { PublishedStatusTypes, RouteNames } from "../../../../enums";
import { useRtn } from "../../../../hooks/useRtn";
import { useWizardStepsManager } from "../../../../hooks/useWizardStepsManager";
import { bindAction } from "../../../../interfaces";
import navigationUtils from "../../../../utils/navigationUtils";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { EmbeddedVideoPublishSuccess } from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import Configure from "../Details/Configure";
import SettingsTab from "../Details/SettingsTab";
import { reset as resetDetails } from "../state/EmbeddedVideoDetailsSlice";
import { resetTags } from "../state/EmbeddedVideosTagsSlice";
import { publishEmbeddedVideo } from "../state/thunks/EmbeddedVideosEntityStateThunk";

enum CreateEmbeddedVideoSteps {
  Configuration,
  Settings,
}

const overviewUrl = `/${RouteNames.contentEmbeddedVideos}`;
const showLastModified = new Set([CreateEmbeddedVideoSteps.Configuration]);

export type CreateEmbeddedVideoProps = PropsFromRedux;

export const CreateEmbeddedVideo = (props: CreateEmbeddedVideoProps) => {
  const { publishEmbeddedVideo, embeddedVideoId, resetTags, onUnmount } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [isValid, setIsValid] = useState(false);
  const [showAddPacks, setShowAddPacks] = useState(false);
  const pageIndexes = [CreateEmbeddedVideoSteps.Configuration, CreateEmbeddedVideoSteps.Settings];

  const [wizardPages, { onNext, onPrevious, onFinish }, subscribeOnActiveIndexChanged] =
    useWizardStepsManager(pageIndexes);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(
    () => {
      return () => {
        resetTags();
        onUnmount();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const handler = (_: CreateEmbeddedVideoSteps, activeStepIndex: CreateEmbeddedVideoSteps) => {
      setActiveTab(activeStepIndex);
    };

    return subscribeOnActiveIndexChanged(handler);
  }, [subscribeOnActiveIndexChanged]);

  const onPublished = () => setShowAddPacks(true);
  useRtn([EmbeddedVideoPublishSuccess], onPublished);

  const handleCancelClick = useCallback(() => {
    navigationUtils.goBackOrDefault(location, navigate, overviewUrl);
  }, [location, navigate]);

  const handleOnFinish = useCallback(() => {
    onFinish?.();
    publishEmbeddedVideo(embeddedVideoId);
  }, [onFinish, publishEmbeddedVideo, embeddedVideoId]);

  const getPublishedStatus = () => (props.isEntityCreated ? PublishedStatusTypes.notPublished : null);

  const onPreviousAsync = async (activeStepIndex: number, nextIndex: number) => {
    onPrevious(activeStepIndex, nextIndex);
  };

  return (
    <>
      <Wizard
        title="Create Video"
        finishButtonLabel="Finish"
        onCancel={handleCancelClick}
        onProgress={onNext}
        onRegressAsync={onPreviousAsync}
        onFinish={handleOnFinish}
        publishedStatus={getPublishedStatus()}
        isSaveInProgress={props.isSaving}
        progressSavedDate={showLastModified.has(activeTab) ? props.dateModified : undefined}
      >
        <Section label="Configuration" className="scrollable-content" isLocked={!isValid} required>
          <Configure
            acceptHandlers={wizardPages[CreateEmbeddedVideoSteps.Configuration]}
            onIsValidChange={setIsValid}
          />
        </Section>
        <Section label="Settings" className="scrollable-content">
          <SettingsTab acceptHandlers={wizardPages[CreateEmbeddedVideoSteps.Settings]} />
        </Section>
      </Wizard>
      <AddAssetsToPackModal
        showModal={showAddPacks}
        onClose={() => navigate(overviewUrl)}
        onComplete={() => {}}
        selectedItemIds={[embeddedVideoId]}
        contentType="EmbeddedVideo"
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const entityState = state.library.embeddedVideos.entitState;
  return {
    embeddedVideoId: entityState.entityId,
    isEntityCreated: entityState.entityId > 0,
    isSaving: entityState.isEntityCommandInProgress,
    dateModified: entityState.lastModifiedDateTime,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  publishEmbeddedVideo: bindAction(publishEmbeddedVideo, dispatch),
  onUnmount: bindAction(resetDetails, dispatch),
  resetTags: bindAction(resetTags, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateEmbeddedVideo);
export default ConnectedComponent;
